import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, Copy, ExternalLink, Save, Loader2, Monitor, Smartphone, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Skeleton } from "../../components/ui/skeleton";
import BlockEditor from "../../components/admin/BlockEditor";
import ImageField from "../../components/admin/ImageField";
import PixelSettings from "../../components/admin/PixelSettings";
import LandingRenderer from "../../components/landing/LandingRenderer";
import { errorMessage, pagesApi } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { activePixelPlatforms } from "../../lib/tracking";
import { cn } from "../../lib/utils";

export const EMPTY_PIXEL = {
  meta_pixel_id: "",
  enabled: false,
  cta_event: "Lead",
  custom_code: "",
  ga4_measurement_id: "",
  ga4_enabled: false,
  google_ads_id: "",
  google_ads_conversion_label: "",
  google_ads_enabled: false,
  tiktok_pixel_id: "",
  tiktok_enabled: false,
  tiktok_cta_event: "ClickButton",
};

const EMPTY = {
  title: "",
  slug: "",
  status: "draft",
  pixel: EMPTY_PIXEL,
  hero: { eyebrow: "Limited time offer", headline: "", subheadline: "", image_url: "", cta_label: "Book Now", cta_url: "/artists" },
  blocks: [],
  footer_note: "",
  campaign: { source: "", medium: "", campaign: "" },
};

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const areaCls = "rounded-none border-border bg-transparent px-3 py-2 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");

/** Scales a 1280px-wide render to fit its container. */
const ScaledPreview = ({ page, mode }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.4);
  const width = mode === "mobile" ? 420 : 1280;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(Math.min(1, el.clientWidth / width)));
    ro.observe(el);
    setScale(Math.min(1, el.clientWidth / width));
    return () => ro.disconnect();
  }, [width]);
  return (
    <div ref={ref} className="relative h-full w-full overflow-auto bg-black/40" data-testid="editor-preview">
      <div style={{ width, zoom: scale }} className="min-h-full bg-background">
        <LandingRenderer page={page} preview />
      </div>
    </div>
  );
};

const PageEditor = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [page, setPage] = useState(isNew ? EMPTY : null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");

  useEffect(() => {
    if (isNew) return;
    pagesApi
      .get(id)
      .then((p) => setPage({ ...EMPTY, ...p, pixel: { ...EMPTY.pixel, ...(p.pixel || {}) }, hero: { ...EMPTY.hero, ...(p.hero || {}) }, campaign: { ...EMPTY.campaign, ...(p.campaign || {}) } }))
      .catch((err) => {
        toast.error(errorMessage(err, "Page not found"));
        navigate("/admin");
      });
  }, [id, isNew, navigate]);

  const patch = useCallback((path, value) => {
    setDirty(true);
    setPage((p) => {
      const next = { ...p };
      if (Array.isArray(path)) {
        const [a, b] = path;
        next[a] = { ...(p[a] || {}), [b]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  }, []);

  const onTitle = (v) => {
    patch("title", v);
    if (isNew && !slugTouched) patch("slug", slugify(v));
  };

  const liveUrl = useMemo(() => (page?.slug ? `${window.location.origin}/lp/${page.slug}` : ""), [page?.slug]);
  const utmUrl = useMemo(() => {
    if (!liveUrl) return "";
    const p = new URLSearchParams();
    const c = page?.campaign || {};
    if (c.source) p.set("utm_source", c.source);
    if (c.medium) p.set("utm_medium", c.medium);
    if (c.campaign) p.set("utm_campaign", c.campaign);
    const qs = p.toString();
    return qs ? `${liveUrl}?${qs}` : liveUrl;
  }, [liveUrl, page?.campaign]);

  const copy = (text) => copyToClipboard(text);

  const save = async () => {
    if (!page.title.trim()) {
      toast.error("Give your page a title");
      return;
    }
    if (!page.hero.headline.trim()) {
      toast.error("Add a hero headline");
      return;
    }
    const px = page.pixel;
    if (px.enabled && !px.meta_pixel_id.trim() && !px.custom_code.trim()) {
      toast.error("Meta Pixel is enabled but no Pixel ID or code was provided");
      return;
    }
    if (px.ga4_enabled && !/^G-[A-Z0-9]{4,}$/i.test(px.ga4_measurement_id.trim())) {
      toast.error("GA4 is enabled but the Measurement ID looks wrong (expected G-XXXXXXXXXX)");
      return;
    }
    if (px.google_ads_enabled && !/^AW-\d{6,}$/i.test(px.google_ads_id.trim())) {
      toast.error("Google Ads is enabled but the Conversion ID looks wrong (expected AW-123456789)");
      return;
    }
    if (px.tiktok_enabled && px.tiktok_pixel_id.trim().length < 6) {
      toast.error("TikTok is enabled but no Pixel ID was provided");
      return;
    }
    setSaving(true);
    try {
      const body = { ...page, slug: page.slug || undefined };
      delete body.stats;
      delete body.id;
      delete body.created_at;
      delete body.updated_at;
      if (isNew) {
        const created = await pagesApi.create(body);
        toast.success("Landing page created");
        setDirty(false);
        navigate(`/admin/pages/${created.id}`, { replace: true });
      } else {
        const updated = await pagesApi.update(id, body);
        setPage((p) => ({ ...p, ...updated }));
        setDirty(false);
        toast.success(updated.status === "published" ? "Saved & live" : "Draft saved");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (!page) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[70vh]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-editor">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground" data-testid="editor-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
          </Link>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="display text-4xl uppercase leading-none tracking-wide md:text-5xl">{isNew ? "New landing page" : page.title || "Untitled"}</h1>
            <Badge variant="outline" className={cn("rounded-none", page.status === "published" ? "border-primary/50 text-primary" : "border-border text-muted-foreground")} data-testid="editor-status-badge">
              {page.status}
            </Badge>
            {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 border border-border px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em]">
            <Switch checked={page.status === "published"} onCheckedChange={(v) => patch("status", v ? "published" : "draft")} data-testid="editor-publish-switch" />
            {page.status === "published" ? "Published" : "Draft"}
          </label>
          {!isNew && (
            <Button asChild variant="outline" className="rounded-none" data-testid="editor-stats-link">
              <Link to={`/admin/pages/${id}/stats`}>
                <BarChart3 className="mr-2 h-4 w-4" /> Stats
              </Link>
            </Button>
          )}
          {!isNew && page.status === "published" && (
            <Button asChild variant="outline" className="rounded-none">
              <a href={`/lp/${page.slug}`} target="_blank" rel="noreferrer" data-testid="editor-view-live">
                <ExternalLink className="mr-2 h-4 w-4" /> View live
              </a>
            </Button>
          )}
          <Button onClick={save} disabled={saving} className="h-10 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="editor-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isNew ? "Create page" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[520px_minmax(0,1fr)]">
        {/* Form */}
        <div className="border border-border bg-card">
          <Tabs defaultValue="content">
            <TabsList className="h-auto w-full justify-start rounded-none border-b border-border bg-transparent p-0">
              {["content", "sections", "pixel"].map((t) => (
                <TabsTrigger key={t} value={t} className="rounded-none border-b-2 border-transparent px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary" data-testid={`editor-tab-${t}`}>
                  {t === "pixel" ? "Pixel & Tracking" : t}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="content" className="space-y-5 p-5">
              <F label="Page title (internal)">
                <Input value={page.title} onChange={(e) => onTitle(e.target.value)} placeholder="Summer Flash Sale" className={inputCls} data-testid="editor-title" />
              </F>
              <F label="URL slug" hint={liveUrl || "Auto-generated from the title"}>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">/lp/</span>
                  <Input
                    value={page.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      patch("slug", slugify(e.target.value));
                    }}
                    placeholder="summer-flash-sale"
                    className={inputCls}
                    data-testid="editor-slug"
                  />
                </div>
              </F>
              <div className="border-t border-border pt-5">
                <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">Hero</p>
                <div className="space-y-4">
                  <F label="Eyebrow">
                    <Input value={page.hero.eyebrow} onChange={(e) => patch(["hero", "eyebrow"], e.target.value)} className={inputCls} data-testid="editor-hero-eyebrow" />
                  </F>
                  <F label="Headline">
                    <Textarea value={page.hero.headline} onChange={(e) => patch(["hero", "headline"], e.target.value)} rows={2} placeholder="Flash Friday: 20% off" className={cn(areaCls, "min-h-[64px]")} data-testid="editor-hero-headline" />
                  </F>
                  <F label="Subheadline">
                    <Textarea value={page.hero.subheadline} onChange={(e) => patch(["hero", "subheadline"], e.target.value)} rows={3} className={cn(areaCls, "min-h-[80px]")} data-testid="editor-hero-subheadline" />
                  </F>
                  <ImageField
                    label="Background image"
                    value={page.hero.image_url}
                    onChange={(v) => patch(["hero", "image_url"], v)}
                    hint="Upload from your computer, pick from the library, or paste a URL. Leave blank to use the studio hero photo."
                    testId="editor-hero-image"
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <F label="CTA label">
                      <Input value={page.hero.cta_label} onChange={(e) => patch(["hero", "cta_label"], e.target.value)} className={inputCls} data-testid="editor-hero-cta-label" />
                    </F>
                    <F label="CTA link">
                      <Input value={page.hero.cta_url} onChange={(e) => patch(["hero", "cta_url"], e.target.value)} placeholder="/artists or https://…" className={inputCls} data-testid="editor-hero-cta-url" />
                    </F>
                  </div>
                </div>
              </div>
              <F label="Footer note (fine print)">
                <Textarea value={page.footer_note} onChange={(e) => patch("footer_note", e.target.value)} rows={2} className={cn(areaCls, "min-h-[64px]")} data-testid="editor-footer-note" />
              </F>
            </TabsContent>

            <TabsContent value="sections" className="p-5">
              <BlockEditor blocks={page.blocks} onChange={(b) => patch("blocks", b)} />
            </TabsContent>

            <TabsContent value="pixel" className="space-y-6 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">Ad platform pixels</p>
                  <p className="mt-1 text-xs text-muted-foreground">Enable any combination. Each fires on page load and again on every CTA click.</p>
                </div>
                <div className="flex flex-wrap justify-end gap-1" data-testid="editor-active-pixels">
                  {activePixelPlatforms(page.pixel).length === 0 ? (
                    <Badge variant="outline" className="rounded-none border-border text-muted-foreground">None active</Badge>
                  ) : (
                    activePixelPlatforms(page.pixel).map((p) => (
                      <Badge key={p} variant="outline" className="rounded-none border-primary/50 text-primary">
                        {p}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
              <PixelSettings pixel={page.pixel} set={(k, v) => patch(["pixel", k], v)} />

              <div className="border-t border-border pt-6">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">Campaign link builder</p>
                <p className="mb-4 text-xs text-muted-foreground">Add UTM tags so the stats page can break down traffic by source.</p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <F label="Source">
                    <Input value={page.campaign.source} onChange={(e) => patch(["campaign", "source"], e.target.value)} placeholder="instagram" className={inputCls} data-testid="editor-utm-source" />
                  </F>
                  <F label="Medium">
                    <Input value={page.campaign.medium} onChange={(e) => patch(["campaign", "medium"], e.target.value)} placeholder="paid_social" className={inputCls} data-testid="editor-utm-medium" />
                  </F>
                  <F label="Campaign">
                    <Input value={page.campaign.campaign} onChange={(e) => patch(["campaign", "campaign"], e.target.value)} placeholder="summer_flash" className={inputCls} data-testid="editor-utm-campaign" />
                  </F>
                </div>
                {utmUrl && (
                  <div className="mt-4 flex items-center gap-2 border border-border bg-background p-3">
                    <code className="flex-1 truncate text-xs text-foreground/80" data-testid="editor-utm-url">{utmUrl}</code>
                    <Button type="button" size="icon" variant="ghost" onClick={() => copy(utmUrl)} aria-label="Copy campaign link" data-testid="editor-copy-utm">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Visits, unique visitors, CTA clicks, devices, referrers and UTM sources are tracked automatically on every published page.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Preview */}
        <div className="flex min-h-[70vh] min-w-0 flex-col overflow-hidden border border-border bg-card xl:sticky xl:top-24 xl:h-[calc(100vh-8rem)]">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Live preview</p>
            <div className="flex items-center gap-1">
              <Button type="button" size="icon" variant="ghost" className={cn("h-8 w-8", previewMode === "desktop" && "text-primary")} onClick={() => setPreviewMode("desktop")} aria-label="Desktop preview" data-testid="preview-desktop">
                <Monitor className="h-4 w-4" />
              </Button>
              <Button type="button" size="icon" variant="ghost" className={cn("h-8 w-8", previewMode === "mobile" && "text-primary")} onClick={() => setPreviewMode("mobile")} aria-label="Mobile preview" data-testid="preview-mobile">
                <Smartphone className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <ScaledPreview page={page} mode={previewMode} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEditor;
